import React, { Component } from 'react'
import PropTypes from 'prop-types'
import * as Sentry from '@sentry/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch'
import { faExchangeAlt } from '@fortawesome/pro-regular-svg-icons/faExchangeAlt'

import Dropdown from '@softwaredev_cegeka/stix-core/dist/components/Dropdown'
import AxiosSelect from '@softwaredev_cegeka/stix-form-select/dist/components/AxiosSelect'
import AuthApi from '@softwaredev_cegeka/auth-api'

import { AuthApi as LocalAuthApiClient } from 'src/api/AuthApi'
import { UserDataContext } from 'src/components/auth/withAuth'

// Styles
import menuStyles from 'src/components/Menu/menu.module.scss'

const apiClient = new LocalAuthApiClient()

class ImpersonateMenu extends Component {
    static contextType = UserDataContext

    constructor(props) {
        super(props)
        this.state = {
            loading: false,
            users: [],
        }
    }

    /**
     * Gets the organisation of the current impersonated user. Used for displaying under the name.
     * @param orgId
     * @returns {null|{label, value}}
     */
    getDefaultOrg(orgId) {
        const defaultOrg = this.props.userOrganizations.find(
            org => org.organization_id === orgId
        )
        if (!defaultOrg) {
            return null
        }
        return { label: defaultOrg.name, value: orgId }
    }

    /**
     * Used for loading organization and user permissions, taking the current app_key into account.
     * @param username
     * @param params
     * @returns {Promise<*|*[]>}
     */
    async loadPermissions(username, params = {}) {
        try {
            const { authApiClient } = this.props

            const permissionResponse = await authApiClient.fetchAppPermissions({
                username: username || this.context.data.username,
                ...params,
            })
            const { app_permissions } = permissionResponse.data
            if (!app_permissions || app_permissions.length === 0) {
                return []
            }
            // Returns an array of objects, but we should only need the permissions of the current app.
            return app_permissions[0].actions || []
        } catch (e) {
            Sentry.captureException(e)
            console.error(e)
        }
    }

    /**
     * Handles an organization impersonation. When impersonating an organization an api call is done fetching that
     * organizations permissions.
     * @param organization
     * @returns {Promise<void>}
     */
    handleOrganizationChange = async organization => {
        if (!organization) {
            // Stop impersonating organization
            try {
                let response = await apiClient.deleteImpersonator()
                const permissions = await this.loadPermissions(null, {
                    app_key: response.data.app_key,
                })
                await this.context.setImpersonatedOrganization(
                    response.headers.authorization,
                    null,
                    permissions
                )
            } catch (e) {
                Sentry.captureException(e)
                console.error(e)
            }
        } else {
            // Start impersonating organization
            try {
                let response = await apiClient.postImpersonator({
                    organisationId: organization.value,
                })
                const permissions = await this.loadPermissions(null, {
                    app_key: response.data.app_key,
                    organization_id: organization.value,
                })
                await this.context.setImpersonatedOrganization(
                    response.headers.authorization,
                    organization,
                    permissions
                )
            } catch (e) {
                Sentry.captureException(e)
                console.error(e)
            }
        }
    }

    renderOrgMenuOption = response => {
        return response.data.tenants.map(org => ({
            value: org.organization_id,
            label: org.name,
            id: org.id,
        }))
    }

    /**
     * Divides user search terms into 2 parts for first and last name.
     * @param input
     * @returns {{part1: string, part2: string}}
     */
    splitSearch(input) {
        let part1 = input
        let part2 = input
        let firstSpaceCharIndex = input.indexOf(' ')
        if (input.includes(' ') && input.length > 0) {
            part1 = input.substr(0, firstSpaceCharIndex)
            part2 = input.substr(firstSpaceCharIndex)
        }
        return { part1, part2 }
    }

    renderOrganizationImpersonate() {
        const DropdownHeader = (
            <>
                <Dropdown.Header>
                    <FontAwesomeIcon icon={faExchangeAlt} />
                    <span className={menuStyles.dropdownPanelHeader}>
                        Change organization
                    </span>
                </Dropdown.Header>
            </>
        )

        // hide this menu when user only has 1 organisation
        if (this.props.userOrganizations.length <= 1) {
            return
        }
        return (
            <>
                {DropdownHeader}
                <AxiosSelect
                    apiConfig={{
                        url: `/users/${this.props.username}/tenants`,
                        params: input => ({
                            name_like: input,
                            per_page: 20,
                            sort: '+name',
                        }),
                    }}
                    apiClient={this.props.authApiClient.client}
                    className={menuStyles.select}
                    label={'Search organization'}
                    srOnly
                    itemRenderer={this.renderOrgMenuOption}
                    isSearchable
                    isClearable
                    menuIsOpen
                    dropdownIndicator={<FontAwesomeIcon icon={faSearch} />}
                    onChange={this.handleOrganizationChange}
                    value={this.context.impersonatedOrganization}
                    placeholder={'Search organizations'}
                />
            </>
        )
    }

    render() {
        const userData = this.context.data
        return !userData?.is_local_user ? (
            <div>{this.renderOrganizationImpersonate()}</div>
        ) : null
    }
}

ImpersonateMenu.propTypes = {
    authApiClient: PropTypes.instanceOf(AuthApi),
    /** Contains all tenants of logged in user. */
    userOrganizations: PropTypes.array,
    username: PropTypes.string,
}

export default ImpersonateMenu
